import {
  Button,
  Col,
  DatePicker,
  Layout,
  Row,
  Tabs,
  Table,
  Dropdown,
  Menu,
} from "antd";
import { Eye, Pen, ChevronDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumber,
  getDateRange,
  getUserRole,
  navigateTo,
} from "../../Utils/Common";
import { hasRole } from "../../Utils/AccessControl";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import dayjs from "dayjs";
import { useTableData } from "../../Utils/useTableData";
import { getRequest } from "../../Utils/ApiCalls";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PettyCashEntry = () => {
  const dateRange = getDateRange("week");
  const [activeTab, setActiveTab] = useState("pending");
  const [departments, setDepartments] = useState<any[]>([]);

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "petty_cash_entries/get",
    initialFilters: {
      date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
      date_to:   formatDate(dateRange[1], "YYYY-MM-DD"),
      status:    "pending",
      page_size: 10,
      search:    "",
    },
    isFormData: true,
  });

  const departmentMap = useMemo(() => {
    const map: Record<string, string> = {};
    departments.forEach((dept: any) => {
      map[String(dept.id)] = dept.name;
    });
    return map;
  }, [departments]);

  const rows = useMemo(
    () =>
      (dataSource || []).map((row: any) => ({
        ...row,
        department:
          row.department ||
          row.department_name ||
          departmentMap[String(row.department_id)] ||
          "",
      })),
    [dataSource, departmentMap]
  );

  useEffect(() => {
    (async () => {
      const response = await getRequest(`departments/get`, {}, true);
      if (response.success) {
        setDepartments(response.data.data ?? []);
      }
    })();
  }, []);

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({
      ...prev,
      status: activeKey,
      page:   1,
    }));
  }

  function getTableColumns() {
    const baseColumns = [
      {
        title:     "Transaction Date",
        dataIndex: "transaction_date",
        key:       "transaction_date",
        render:    (date: any) => formatDate(date, "MMM DD, YYYY"),
      },
      {
        title:     "Account",
        dataIndex: "account",
        key:       "account",
      },
      {
        title:     "Department",
        dataIndex: "department",
        key:       "department",
        render:    (value: any) => value || "-",
      },
      {
        title:     "Branch",
        dataIndex: "branch_name",
        key:       "branch_name",
      },
      {
        title:     "Requested By",
        dataIndex: "requested_by",
        key:       "requested_by",
      },
      {
        title:     "Amount",
        dataIndex: "amount",
        key:       "amount",
        align:     "right" as const,
        render:    (amount: any) => formatNumber(amount),
      },
      {
        title:  "Remarks",
        dataIndex: "remarks",
        key:    "remarks",
        render: (remarks: any) => remarks ?? "-",
      },
      {
        title:     "Added By",
        dataIndex: "added_by_name",
        key:       "added_by_name",
      },
    ];

    switch (activeTab) {
      case "pending":
        return [
        ...baseColumns,
        ...(
          hasRole(getUserRole(), "custodian", "supervisor")
            ? [{
                title: "",
                key: "action",
                width: 10,
                align: "center" as const,
                render: (_text: any, record: any) => (
                  <Dropdown
                    overlay={
                      <Menu>
                        {hasRole(getUserRole(), "custodian") && (
                          <>
                            <Menu.Item
                              key="view"
                              icon={<Eye strokeWidth={1.5} size={15} />}
                              onClick={() => navigateTo(`/petty-cash-entry/view/${record.id}`)}
                            >
                              View
                            </Menu.Item>
                            <Menu.Item
                              key="edit"
                              icon={<Pen strokeWidth={1.5} size={15} />}
                              onClick={() => navigateTo(`/petty-cash-entry/edit/${record.id}`)}
                            >
                              Edit
                            </Menu.Item>
                          </>
                        )}

                        {hasRole(getUserRole(), "supervisor") && (
                          <Menu.Item
                            key="review"
                            icon={<Eye strokeWidth={1.5} size={15} />}
                            onClick={() => navigateTo(`/petty-cash-entry/review/${record.id}`)}
                          >
                            Review
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                    trigger={["click"]}
                  >
                    <Button>
                      Actions <ChevronDown strokeWidth={0.5} />
                    </Button>
                  </Dropdown>
                ),
              }]
            : []
        ),
      ];

      case "approved":
        return [
          ...baseColumns,
          {
            title:     "Approved By",
            dataIndex: "approver_name",
            key:       "approver_name",
          },
          {
            title:  "Action",
            key:    "action",
            align:  "center" as const,
            render: (_text: any, record: any) => (
              <Button
                type="primary"
                size="small"
                onClick={() => navigateTo(`/petty-cash-entry/view/${record.id}`)}
              >
                View
              </Button>
            ),
          },
        ];

      case "disapproved":
        return [
          ...baseColumns,
          {
            title:     "Disapproved By",
            dataIndex: "disapprover_name",
            key:       "disapprover_name",
          },
          {
            title:     "Reason",
            dataIndex: "reason",
            key:       "reason",
          },
          {
            title:  "Action",
            key:    "action",
            align:  "center" as const,
            render: (_text: any, record: any) => (
              <Button
                type="primary"
                size="small"
                onClick={() => navigateTo(`/petty-cash-entry/view/${record.id}`)}
              >
                View
              </Button>
            ),
          },
        ];

      default:
        return baseColumns;
    }
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2 || !value[0] || !value[1]) {
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: "",
          date_to:   "",
          page:      1,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0].format("YYYY-MM-DD"),
        date_to:   value[1].format("YYYY-MM-DD"),
        page:      1,
      }));
      return;
    }

    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value ?? "",
      page:   1,
    }));
  }

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Petty Cash - Cash In"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "custodian", "admin")}
              addBtnText="Petty Cash"
              onCreate={() => navigateTo("/petty-cash-entry/add")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value ?? "",
                  page:   1,
                }))
              }
            />
          </Col>
          <Col xs={24} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-3">
            <RangePicker
              value={[
                filterConfig.date_from ? dayjs(filterConfig.date_from) : null,
                filterConfig.date_to   ? dayjs(filterConfig.date_to)   : null,
              ]}
              format="MMMM D, YYYY"
              onChange={(e) => handleFilterChange(e, "dates")}
              allowClear
              className="w-full"
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onChange={onChangeTab}
                >
                  <TabPane tab="Cash In Request" key="pending">
                    <Table
                      columns={getTableColumns()}
                      dataSource={rows}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current:          filterConfig.page,
                        total:            dataSize,
                        showSizeChanger:  true,
                        onChange:         (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={rows}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current:          filterConfig.page,
                        total:            dataSize,
                        showSizeChanger:  true,
                        onChange:         (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Disapproved" key="disapproved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={rows}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current:          filterConfig.page,
                        total:            dataSize,
                        showSizeChanger:  true,
                        onChange:         (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default PettyCashEntry;